import React, { useState } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';

/**
 * HighlightedText Component
 * 
 * Displays text with smart highlighting of key metrics:
 * - Budget numbers (green with $ icon)
 * - Box office figures (blue with chart icon)
 * - Film titles (bold and colored)
 * - Expandable/collapsible for long content
 */

const HighlightedText = ({ content, maxLength = 500, title }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!content) return null;

  const shouldTruncate = content.length > maxLength;
  const displayText = (shouldTruncate && !expanded) ? content.substring(0, maxLength) + '...' : content;

  return (
    <Box sx={{ mb: 3 }}>
      {title && (
        <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
      )}
      
      <Box sx={{ position: 'relative' }}>
        <Typography
          component="div"
          sx={{
            color: brandColors.primary,
            opacity: 0.85,
            lineHeight: 1.8,
            fontSize: '1rem'
          }}
        >
          {highlightText(displayText)}
        </Typography>

        {shouldTruncate && (
          <Button
            size="small"
            endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
            onClick={() => setExpanded(!expanded)}
            sx={{
              mt: 1,
              color: brandColors.secondary,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {expanded ? 'Show Less' : 'Read More'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

/**
 * Highlight key metrics in text
 */
const overlapsMoneyMatch = (matches, index, length) => {
  for (let i = 0; i < matches.length; i += 1) {
    const candidate = matches[i];
    if (
      candidate.type === 'money'
      && index >= candidate.start
      && index < candidate.end
    ) {
      return true;
    }
  }
  return false;
};

function highlightText(text) {
  const parts = [];
  let lastIndex = 0;
  let partKey = 0;

  // Pattern for budget/box office numbers: $XXM, $XX-XXM, etc.
  const moneyRegex = /\$[\d,]+-?\$?[\d,]*M(?:illion)?(?:\s*(?:domestic|worldwide|budget|B\.O\.))?/gi;
  
  // Pattern for film titles in italics or bold: *Title* or **Title**
  const filmTitleRegex = /\*{1,2}([^*]+?)\*{1,2}(?:\s*\(\d{4}\))?/g;
  
  // Pattern for percentages and numbers
  const percentRegex = /\d+(?:\.\d+)?%/g;

  // Combine all matches
  const allMatches = [];
  
  // Find money matches
  let match;
  while ((match = moneyRegex.exec(text)) !== null) {
    allMatches.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
      type: 'money'
    });
  }

  // Find film title matches
  filmTitleRegex.lastIndex = 0;
  while ((match = filmTitleRegex.exec(text)) !== null) {
    allMatches.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
      displayText: match[1],
      type: 'film'
    });
  }

  // Find percentage matches
  percentRegex.lastIndex = 0;
  while ((match = percentRegex.exec(text)) !== null) {
    const inMoney = overlapsMoneyMatch(allMatches, match.index, match[0].length);
    if (!inMoney) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        type: 'percent'
      });
    }
  }

  // Sort by position
  allMatches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep first)
  const filteredMatches = [];
  for (const m of allMatches) {
    const overlaps = filteredMatches.some(fm => 
      (m.start >= fm.start && m.start < fm.end) ||
      (m.end > fm.start && m.end <= fm.end)
    );
    if (!overlaps) {
      filteredMatches.push(m);
    }
  }

  // Build parts array
  for (let i = 0; i < filteredMatches.length; i++) {
    const match = filteredMatches[i];
    
    // Add text before match
    if (match.start > lastIndex) {
      parts.push(
        <span key={partKey++}>{text.substring(lastIndex, match.start)}</span>
      );
    }

    // Add highlighted match
    if (match.type === 'money') {
      parts.push(
        <Chip
          key={partKey++}
          label={match.text}
          size="small"
          icon={<MoneyIcon />}
          sx={{
            backgroundColor: '#388e3c15',
            color: '#388e3c',
            fontWeight: 600,
            fontSize: '0.9rem',
            height: 24,
            mx: 0.5,
            '& .MuiChip-icon': { color: '#388e3c', fontSize: '1rem' }
          }}
        />
      );
    } else if (match.type === 'film') {
      parts.push(
        <span
          key={partKey++}
          style={{
            fontWeight: 600,
            color: brandColors.secondary,
            fontStyle: 'italic'
          }}
        >
          {match.displayText || match.text}
        </span>
      );
    } else if (match.type === 'percent') {
      parts.push(
        <span
          key={partKey++}
          style={{
            fontWeight: 700,
            color: brandColors.secondary,
            backgroundColor: `${brandColors.secondary}15`,
            padding: '2px 6px',
            borderRadius: 4
          }}
        >
          {match.text}
        </span>
      );
    }

    lastIndex = match.end;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(
      <span key={partKey++}>{text.substring(lastIndex)}</span>
    );
  }

  return parts.length > 0 ? parts : text;
}

export default HighlightedText;
export { highlightText };

